import { useTranslation } from 'react-i18next';
import config from '../../../../../app/config';
import React, { useEffect } from 'react';
import _ from 'lodash';
import moment from 'moment/moment';
import * as _debounce from 'lodash/debounce';
import div from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import { emitChangedWait, getObsScore, obsStatuses, obsTypes } from '../obsUtils';
import ObsNumericStepper from './obsNumericStepper';
import renderAcceptSection from './renderAcceptSection';
import AgyleDictionaryField from '../../../../../components/ui/selector/agyleDictionaryField';
import { FormLabel } from '@mui/material';
import { obsStyles, obsStylesMews } from '../obsStyle';

function ObsTemperature(props) {
    const classesMews = obsStylesMews();
    const classes = obsStyles();
    const { t } = useTranslation();
    const { type, ageRangeYears } = props;
    const allowDefaultValue = config?.observations?.defaultValues;
    const disableAcceptButtons = config?.observations?.disableAcceptButtons;

    const [status, setStatus] = React.useState(props.status || obsStatuses.new);
    const [temperature, setTemperature] = React.useState(allowDefaultValue ? props.temperature : null);
    const [place, setPlace] = React.useState(allowDefaultValue ? props.place : null);
    const [score, setScore] = React.useState(props.score);
    const [accepted, setAccepted] = React.useState(props.accepted);
    const [acceptDate, setAcceptDate] = React.useState(props.acceptDate);

    const places = [
        {
            id: 'axilla',
            description: t('Axilla'),
            default: true,
        },
        {
            id: 'tympanic',
            description: t('Tympanic'),
        },
        {
            id: 'skin',
            description: t('Skin'),
        },
    ];

    // For Mobile view
    useEffect(() => {
        if (props.ipodView && props.status !== obsStatuses.new) {
            setTemperature(props.temperature);
            setPlace(props.place);
        }
    }, []);

    const setDefaults = () => {
        setTemperature(36.6);
        if (type === obsTypes.pews) {
            let def = _.find(places, (item) => !!item.default);
            if (def) {
                setPlace(def);
            }
        } else {
            setPlace(null);
        }
    };

    useEffect(() => {
        if (type === obsTypes.pews && !place && allowDefaultValue) {
            let def = _.find(places, (item) => !!item.default);
            if (def) {
                setPlace(def);
            }
        }
    }, [type]);

    useEffect(() => {
        if ((_.isUndefined(temperature) || _.isNaN(temperature)) && allowDefaultValue) {
            if (_.isUndefined(props.temperature) || props.temperature == null || _.isNaN(props.temperature)) {
                setTemperature(36.6);
            } else {
                setTemperature(props.temperature);
            }
        }
    }, [props.temperature]);

    const clearAccepted = () => {
        setStatus(obsStatuses.new);
        setAccepted(false);
        setAcceptDate(null);
    };

    const resetObs = () => {
        if (!allowDefaultValue) {
            setTemperature(null);
            if (type === obsTypes.pews) {
                setPlace(null);
            }
        } else {
            setDefaults();
        }
        clearAccepted();
    };

    const acceptObs = () => {
        if (status === obsStatuses.new) {
            setStatus(obsStatuses.completed);
        }
        setAcceptDate(moment());
        setAccepted(true);
    };

    useEffect(() => {
        const checkValue =
            (!_.isNull(temperature) && status === obsStatuses.new) || (status !== obsStatuses.completed && status !== obsStatuses.new);

        if (disableAcceptButtons && checkValue) {
            if (type === obsTypes.pews) {
                const checkPlace =
                    (!_.isNull(place) && status === obsStatuses.new) || (status !== obsStatuses.completed && status !== obsStatuses.new);
                if (checkPlace) {
                    acceptObs();
                }
            } else {
                acceptObs();
            }
        }
    }, [temperature, place, status]);

    const lazyEmitOnChange = React.useMemo(() => {
        return _debounce((value, onChange) => {
            // console.log("lazyEmitOnChange", value);
            onChange(value);
        }, emitChangedWait);
    }, []);

    const emitChanged = () => {
        if (props.onChange) {
            // console.log("emitChanged");
            if (type === obsTypes.pews) {
                lazyEmitOnChange(
                    {
                        status,
                        temperature,
                        place,
                        score,
                        accepted,
                        acceptDate,
                    },
                    props.onChange,
                );
            } else {
                lazyEmitOnChange(
                    {
                        status,
                        temperature,
                        // place,
                        score,
                        accepted,
                        acceptDate,
                    },
                    props.onChange,
                );
            }
        }
    };

    useEffect(() => {
        emitChanged();
    }, [status, temperature, place, score, accepted, acceptDate]);

    const updateScore = () => {
        let newScore = 0;
        if (type === obsTypes.news) {
            if (status === obsStatuses.notRecorded || status === obsStatuses.patientRefused || status === obsStatuses.equipment) {
                newScore = 0;
            } else if (status === obsStatuses.patientCondition) {
                newScore = 3;
            } else if (temperature) {
                if (temperature < 35.1) {
                    newScore = 3;
                } else if (temperature >= 35.1 && temperature < 36.1) {
                    newScore = 1;
                } else if (temperature >= 36.1 && temperature < 38.1) {
                    newScore = 0;
                } else if (temperature >= 38.1 && temperature < 39.1) {
                    newScore = 1;
                } else {
                    newScore = 2;
                }
            }
        }
        if (type === obsTypes.mews) {
            newScore = getObsScore('temperature', type, ageRangeYears, temperature);
        }
        setScore(newScore);
    };

    useEffect(() => {
        updateScore();
    }, [status, temperature, place]);

    const onPlaceChange = (value) => {
        setPlace(value);
        clearAccepted();
    };

    const onTemperatureChange = (value) => {
        setTemperature(value);
        clearAccepted();
    };

    const onStatusChange = (value) => {
        clearAccepted();
        if (value && value !== status) {
            setStatus(value);
        }
    };

    return (
        <div className={classes.obsRoot}>
            <div className={classes.obsContent}>
                <Grid container alignItems={'center'} spacing={2}>
                    {type === obsTypes.pews && (
                        <Grid item xs={12} className={classes.component} style={{ marginLeft: 15 }}>
                            <Grid container direction="row" justifyContent="space-between" alignItems="center" style={{ marginBottom: 15 }}>
                                <Grid item xs={12}>
                                    <FormLabel className={classes.label}>{t('Measuring Place')}</FormLabel>
                                </Grid>

                                <Grid item xs={12}>
                                    <AgyleDictionaryField
                                        onChange={(_, value) => onPlaceChange(value)}
                                        options={places}
                                        value={place}
                                        label={t('')}
                                        getOptionLabel={(option) => option.description}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        extended
                                        noBorder={false}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <ObsNumericStepper
                            value={temperature}
                            minValue={20}
                            maxValue={45}
                            label={t('°C')}
                            precision={1}
                            step={0.1}
                            onChange={onTemperatureChange}
                        />
                    </Grid>

                    {renderAcceptSection({
                        classes: type === obsTypes.mews ? classesMews : classes,
                        t,
                        hideScore: type === obsTypes.pews || type === obsTypes.neuro,
                        score,
                        status,
                        onStatusChange,
                        resetObs,
                        accepted,
                        acceptObs,
                        acceptDate,
                        acceptDisabled: _.isNull(temperature),
                        showStatusNR: type === obsTypes.pews,
                        hideStatuses: type === obsTypes.neuro,
                        disableAcceptButtons,
                    })}
                </Grid>
            </div>
        </div>
    );
}

ObsTemperature.propTypes = {
    type: PropTypes.oneOf([obsTypes.news, obsTypes.pews, obsTypes.neuro, obsTypes.mews]),
    ageRangeYears: PropTypes.string,
    status: PropTypes.string,
    score: PropTypes.number,
    temperature: PropTypes.number,
    accepted: PropTypes.bool,
    acceptDate: PropTypes.instanceOf(moment),
    onChange: PropTypes.func,
};
ObsTemperature.defaultProps = {
    type: obsTypes.news,
    status: obsStatuses.new,
    score: 0,
    temperature: 36.6,
    accepted: false,
    acceptDate: null,
};

export default React.memo(ObsTemperature);
